import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { API, axios } from '../../utils/api';
import { salvaBlob } from '../../utils/documenti';
import Icona from '../../components/cliente/Icone';
import { GENERICI, ROTTE, percento, perche, soldi, tagStato } from '../../components/staff/Provvigione';
import '../../components/staff/provvigioni.css';

// /admin/provvigioni (solo staff): i riepiloghi delle provvigioni — l'anno e mese per mese, previsto / fatturato /
// incassato — con l'esportazione CSV per il commercialista, e sotto le pratiche una per una.
// Il cliente non vede niente di tutto questo: è una pagina dello staff.
//
// Chi vede cosa lo decide il server (backend/provvigioni.py): al collaboratore gli importi sono la SUA quota
// (`solo_quota: true`, niente compenso del cliente, niente agenzie) e il CSV non c'è, perché è solo dell'admin.
// I conti non si rifanno qui: si mostrano i totali del server.
// Dati: GET /api/provvigioni/riepilogo, /api/provvigioni/pratiche, /api/provvigioni.csv.

const MESI = ['gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno', 'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'];

// Le tre colonne dei riepiloghi, nell'ordine in cui si leggono
export const COLONNE = [['previsto', 'Previsto', 'oro'], ['fatturato', 'Fatturato', 'blu'], ['incassato', 'Incassato', 'verde']];

const PROBLEMI = {
  ...GENERICI,
  400: 'Di quell\'anno non ci sono riepiloghi.',
  403: 'Questi riepiloghi non si possono vedere: ognuno vede i suoi, il responsabile anche quelli del suo gruppo.',
  404: 'Non trovo i riepiloghi delle provvigioni: le provvigioni non sono ancora collegate al server.',
  vuoto: 'Il server ha risposto senza i riepiloghi. La pagina è pronta e li mostra appena arrivano.',
  errore: 'Non riesco a caricare i riepiloghi delle provvigioni. Riprovate tra poco.',
};

// Gli ultimi anni, dal più recente: servono appena si apre la pagina, prima che il server dica quali ha davvero
export const anniRecenti = (oggi = new Date()) => Array.from({ length: 4 }, (_, i) => String(oggi.getFullYear() - i));

const sembraRiepilogo = (d) => Boolean(d) && typeof d === 'object' && !Array.isArray(d) && (Array.isArray(d.mesi) || typeof d.totale === 'object');

// Gli stessi campi non sono la stessa cosa: all'admin sono gli importi della casa, a chi vede solo le quote sono le
// quote (la propria, e quelle del gruppo per il responsabile). Le parole lo dicono.
const QUOTE = { previsto: 'quota prevista', fatturato: 'quota fatturata', incassato: 'quota incassata' };
const nomeTotale = (chiave, nome, soloQuota) => (soloQuota ? QUOTE[chiave] : nome.toLowerCase());

const Provvigioni = () => {
  const [params, setParams] = useSearchParams();
  const [dati, setDati] = useState(null);
  const [pratiche, setPratiche] = useState(null);
  const [anni, setAnni] = useState(() => anniRecenti());
  const [problema, setProblema] = useState(null);

  const anno = params.get('anno') || anniRecenti()[0];
  const mese = params.get('mese') || '';

  useEffect(() => {
    let vivo = true;
    setDati(null);
    setPratiche(null);
    setProblema(null);
    axios.get(`${API}${ROTTE.riepilogo}`, { params: { anno } })
      .then((r) => {
        if (!vivo) return;
        if (!sembraRiepilogo(r.data)) { setProblema(PROBLEMI.vuoto); return; }
        setDati(r.data);
        if (Array.isArray(r.data.anni) && r.data.anni.length) setAnni(r.data.anni.map(String).reverse());
      })
      .catch((e) => { if (vivo) setProblema(perche(e, PROBLEMI)); });
    // Le pratiche sono una seconda richiesta: se non arrivano, i totali si vedono lo stesso
    axios.get(`${API}${ROTTE.pratiche}`, { params: { anno: mese ? undefined : anno, mese: mese || undefined } })
      .then((r) => { if (vivo && Array.isArray(r.data?.pratiche)) setPratiche(r.data); })
      .catch(() => { if (vivo) setPratiche(false); });
    return () => { vivo = false; };
  }, [anno, mese]);

  const cambia = (nuovi) => setParams({ anno, ...(mese ? { mese } : {}), ...nuovi });

  // Il CSV lo scrive il server (";" e BOM, si apre con Excel), con l'anno e il mese che si stanno guardando
  const scarica = async () => {
    try {
      const r = await axios.get(`${API}${ROTTE.csv}`, { params: { anno: mese ? undefined : anno, mese: mese || undefined }, responseType: 'blob' });
      salvaBlob(r.data, `provvigioni-${mese || anno}.csv`);
    } catch (e) { toast.error(perche(e, { ...PROBLEMI, errore: 'CSV non scaricato' })); }
  };

  const elencoAnni = anni.includes(String(anno)) ? anni : [String(anno), ...anni];
  const soloQuota = Boolean(dati?.solo_quota);
  const testa = (
    <div className="head r">
      <div>
        <div className="k">Provvigioni</div>
        <h1>Previsto, fatturato, incassato</h1>
        <p>{soloQuota
          ? 'Le quote, mese per mese: quello che spetta a voi (e al vostro gruppo, se ne guidate uno). Il compenso del cliente e i conti con le agenzie li vede chi se ne occupa.'
          : 'Il riepilogo dell\'anno e mese per mese, con il CSV per il commercialista. Pagina dello staff: il cliente non la vede.'}</p>
      </div>
      <div className="strumenti">
        <label className="anno">Anno
          <select value={anno} onChange={(e) => cambia({ anno: e.target.value, mese: '' })} aria-label="Anno">
            {elencoAnni.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </label>
        <label className="anno">Mese
          <select value={mese} onChange={(e) => cambia({ mese: e.target.value })} aria-label="Mese">
            <option value="">tutto l'anno</option>
            {MESI.map((n, i) => <option key={n} value={`${anno}-${String(i + 1).padStart(2, '0')}`}>{n}</option>)}
          </select>
        </label>
        {dati && !soloQuota && <button type="button" className="btn p" onClick={scarica}><Icona nome="scarica" />CSV</button>}
      </div>
    </div>
  );

  if (problema) {
    return (
      <main className="st wrap pagina-provvigioni" data-testid="provvigioni">
        {testa}
        <p className="vuoto" data-testid="provvigioni-problema">{problema}</p>
      </main>
    );
  }
  if (!dati) {
    return (
      <main className="st wrap pagina-provvigioni" data-testid="provvigioni">
        {testa}
        <p className="vuoto">Carico i riepiloghi…</p>
      </main>
    );
  }

  // I totali del periodo che si sta guardando: l'anno, o il mese scelto (le righe dei mesi le manda il server)
  const mesi = dati.mesi || [];
  const totale = (mese ? mesi.find((m) => m.mese === mese) : dati.totale) || {};
  const righe = pratiche?.pratiche || [];

  return (
    <main className="st wrap pagina-provvigioni" data-testid="provvigioni">
      {testa}

      <div className="totali r" data-testid="totali-provvigioni">
        {COLONNE.map(([k, nome, colore]) => (
          <div key={k} className={colore}><b>{soldi(totale[k])}</b><span>{nomeTotale(k, nome, soloQuota)}</span></div>
        ))}
        {/* «quota» è la fetta dei collaboratori sulle pratiche del periodo, contata una volta sola: c'è anche nei
            mesi che hanno solo la fattura o l'incasso (i server più vecchi mandavano solo «quote») */}
        {!soloQuota && (totale.quota !== undefined || totale.quote)
          && <div><b>{soldi(totale.quota ?? totale.quote.previsto)}</b><span>ai collaboratori</span></div>}
        {!soloQuota && totale.netto !== undefined && <div><b>{soldi(totale.netto)}</b><span>netto (spese tolte)</span></div>}
        <div><b>{totale.pratiche ?? 0}</b><span>{totale.pratiche === 1 ? 'pratica' : 'pratiche'}</span></div>
      </div>
      {soloQuota && <p className="imp-nota" data-testid="solo-quota">Sono quote: di ogni pratica vedete la fetta del collaboratore, mai il compenso del cliente né i conti con l'agenzia.</p>}

      <section className="card r" data-testid="mesi-provvigioni">
        <div className="ttl"><div className="i"><Icona nome="torta" /></div><h2>Mese per mese <small>{soloQuota ? `quote · ${anno}` : anno}</small></h2></div>
        <div className="prov-tabella" role="table">
          <div className="riga mesi testa" role="row">
            <span>Mese</span>{COLONNE.map(([k, nome]) => <span key={k} className="cifra">{nome}</span>)}<span className="cifra">Pratiche</span>
          </div>
          {mesi.map((m) => (
            <div className={`riga mesi${m.mese === mese ? ' totale' : ''}`} role="row" key={m.mese} data-testid={`mese-${m.mese}`}>
              <span>{m.etichetta || m.mese}</span>
              {COLONNE.map(([k]) => <span key={k} className="cifra">{m[k] ? soldi(m[k]) : '—'}</span>)}
              <span className="cifra">{m.pratiche || '—'}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="card r" data-testid="pratiche-provvigioni" style={{ marginTop: '1rem' }}>
        <div className="ttl"><div className="i b"><Icona nome="euro" /></div>
          <h2>Le pratiche <small>{mese ? MESI[Number(mese.split('-')[1]) - 1] : anno}</small></h2></div>
        {pratiche === null && <p className="vuoto">Carico le pratiche…</p>}
        {pratiche === false && <p className="vuoto" data-testid="pratiche-problema">Le pratiche non sono arrivate: i totali qui sopra restano quelli del server.</p>}
        {pratiche && !righe.length && <p className="vuoto">Nessuna pratica nel periodo scelto.</p>}
        {righe.length > 0 && (
          <div className="prov-tabella" role="table">
            <div className="riga pratiche testa" role="row">
              <span>Cliente</span><span>{soloQuota ? 'Quando' : 'Agenzia'}</span>
              <span className="cifra">{soloQuota ? 'Quota' : 'Incasso lordo'}</span><span>A che punto</span><span />
            </div>
            {righe.map((p) => {
              const stato = tagStato(p.stato || p.quota?.stato);
              const quando = p.quota?.data || p.data;
              return (
                <div className="riga pratiche" role="row" key={p.proposta_id} data-testid={`pratica-${p.proposta_id}`}>
                  <span>{p.cliente || '—'}</span>
                  <span>{soloQuota
                    ? (quando ? new Date(quando).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' }) : '—')
                    : (p.agenzia || '—')}</span>
                  <span className="cifra">{soloQuota ? soldi(p.quota?.importo) : (p.conti ? soldi(p.conti.incasso_lordo) : '—')}</span>
                  <span>{p.c_e === false && !soloQuota ? <span className="muto">da compilare</span> : <span className={`tag ${stato[0]}`}>{stato[1]}</span>}</span>
                  <span>{p.cliente_id ? <Link className="btn sm" to={`/admin/clienti/${p.cliente_id}#proposte`}>Apri</Link> : null}</span>
                </div>
              );
            })}
          </div>
        )}
        {!soloQuota && righe.some((p) => p.conti?.quota_percentuale) && (
          <p className="imp-nota">La quota del collaboratore è una percentuale dell'incasso netto: {righe.filter((p) => p.conti?.quota_percentuale)
            .map((p) => percento(p.conti.quota_percentuale)).filter((v, i, a) => a.indexOf(v) === i).join(', ')}.</p>
        )}
      </section>
    </main>
  );
};

export default Provvigioni;
